import { useState } from 'react';
import { orgsApi } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { Loader2, Send, CheckCircle2, Building2 } from 'lucide-react';

export function RequestAccessDialog({ open, onOpenChange }) {
  const [orgName, setOrgName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [ndisNum, setNdisNum] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setOrgName(''); setName(''); setEmail(''); setPhone('');
    setNdisNum(''); setMessage(''); setDone(false);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await orgsApi.submitAccessRequest({
        org_name: orgName.trim(),
        contact_name: name.trim(),
        contact_email: email.trim(),
        contact_phone: phone.trim() || undefined,
        ndis_registration_number: ndisNum.trim() || undefined,
        message: message.trim() || undefined,
      });
      setDone(true);
      toast.success('Request sent — check your email for confirmation.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg bg-white" data-testid="request-access-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-teal-600" /> Request access to Visitex
          </DialogTitle>
          <DialogDescription>
            Tell us about your NDIS organisation. We&apos;ll review and email you a one-time
            QR invitation link to set up your isolated workspace.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-8 text-center space-y-3" data-testid="request-access-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">You&apos;re on the list</h3>
            <p className="text-sm text-slate-600">
              We&apos;ve sent a confirmation to <strong>{email}</strong>. The Visitex team will
              be in touch with your QR invitation shortly.
            </p>
            <Button
              onClick={() => handleClose(false)}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="request-access-close"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="request-access-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2 space-y-1">
                <Label htmlFor="ra-org">Organisation name</Label>
                <Input id="ra-org" data-testid="ra-org-name" value={orgName}
                  onChange={(e) => setOrgName(e.target.value)} required minLength={2} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ra-name">Your name</Label>
                <Input id="ra-name" data-testid="ra-contact-name" value={name}
                  onChange={(e) => setName(e.target.value)} required minLength={2} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ra-email">Work email</Label>
                <Input id="ra-email" type="email" data-testid="ra-contact-email" value={email}
                  onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ra-phone">Phone (optional)</Label>
                <Input id="ra-phone" data-testid="ra-contact-phone" value={phone}
                  onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ra-ndis">NDIS registration # (optional)</Label>
                <Input id="ra-ndis" data-testid="ra-ndis" value={ndisNum}
                  onChange={(e) => setNdisNum(e.target.value)} />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <Label htmlFor="ra-msg">Anything you&apos;d like to tell us? (optional)</Label>
                <Textarea id="ra-msg" data-testid="ra-message" rows={3} maxLength={1000}
                  value={message} onChange={(e) => setMessage(e.target.value)}
                  placeholder="Number of workers, type of supports, etc." />
              </div>
            </div>
            <Button
              type="submit" disabled={submitting}
              className="w-full bg-teal-600 hover:bg-teal-700"
              data-testid="ra-submit"
            >
              {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>) :
                (<><Send className="w-4 h-4 mr-2" />Send request</>)}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
